import { useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { FormCard } from '../login';
import { Eye, EyeOff } from 'lucide-react';
import PhoneInput from '../../components/PhoneInput';
import { Backdrop, CircularProgress, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { CustomInput } from '../../components/CustomInput';
import { Button } from '../../components/Button';
import { HOST_API } from '../../config/hosts';
import { toast } from 'react-toastify';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const validationSchema = Yup.object({
  full_name: Yup.string().min(3, 'Nome muito curto').required('Nome é obrigatório'),
  email: Yup.string().email('Email inválido').required('Email é obrigatório'),
  phone: Yup.string().length(11, 'Telefone incompleto').required('Telefone é obrigatório'),
  password: Yup.string()
    .min(6, 'Senha deve ter pelo menos 6 caracteres')
    .required('Senha é obrigatória'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'As senhas não coincidem')
    .required('Confirme sua senha'),
});

export function RegisterForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successSent, setSuccessSent] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  const handleSubmit = async (values) => {
    if (isLoading) return;
    setIsLoading(true);

    const payload = {
      ...values,
      username: values.email,
      terms_date: acceptTerms ? new Date() : null,
    };

    delete payload.email;
    delete payload.confirmPassword;
    delete payload.acceptTerms;

    try {
      const response = await fetch(`${HOST_API}/v1/auth/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      let message = null;

      if (response.status === 200 && data.status === 'OK') {
        setSuccessSent(true);
      } else if (data.exists) {
        if (data.usernameExists) {
          message = t('messages.emailAlreadyExists');
        } else if (data.phoneExists) {
          message = t('messages.phoneAlreadyExists');
        } else {
          message = t('messages.userExists');
        }
      } else {
        message = t('messages.errorCreateUser');
      }

      if (message) {
        toast.warning(message, { theme: 'dark' });
      }
    } catch (error) {
      toast.error(t('messages.errorCreateUser'), { theme: 'dark' });
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      full_name: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  return (
    <>
      {successSent ? (
        <FormCard>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '18px',
                borderRadius: '50%',
                backgroundColor: '#E9F7DF',
              }}
            >
              <CheckCircleIcon sx={{ color: '#83D84D', fontSize: 48 }} />
            </div>
            <Typography
              sx={{ fontSize: 24, fontWeight: 700, color: '#2C2C2C', fontFamily: 'Ubuntu' }}
            >
              {t('auth.registerSuccess')}
            </Typography>

            <Typography
              sx={{ fontSize: 14, fontWeight: 400, color: '#575757', fontFamily: 'Ubuntu' }}
            >
              {t('auth.canAccessAcount')}
            </Typography>

            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full bg-white border border-[#83d84d] text-[#83d84d] hover:bg-[#f6fff0] active:bg-[#ecffe0] font-semibold py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {t('auth.logIn')}
            </button>
          </div>
        </FormCard>
      ) : (
        <FormCard title={t('auth.createAccount')}>
          <form onSubmit={formik.handleSubmit}>
            <CustomInput
              label={t('forms.fullName')}
              id="full_name"
              name="full_name"
              type="text"
              placeholder={t('forms.fullNamePlaceholder')}
              value={formik.values.full_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.full_name && formik.errors.full_name}
              helperText={formik.touched.full_name && formik.errors.full_name}
            />

            <CustomInput
              label={t('auth.email')}
              name="email"
              type="email"
              placeholder={t('auth.userPlaceholder')}
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && formik.errors.email}
              helperText={formik.touched.email && formik.errors.email}
            />

            <PhoneInput
              label={t('forms.phone')}
              name="phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  fontSize: '14px',
                  '& fieldset': { borderColor: '#d1d5db' },
                  '&:hover fieldset': { borderColor: '#83d84d' },
                  '&.Mui-focused fieldset': { borderColor: '#83d84d' },
                },
                '& .MuiInputBase-input': { padding: '12px 16px' },
                '& .MuiFormHelperText-root': { marginLeft: 0 },
              }}
            />

            <CustomInput
              label={t('auth.password')}
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && formik.errors.password}
              helperText={formik.touched.password && formik.errors.password}
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                </button>
              }
            />

            <CustomInput
              label={t('auth.confirmPassword')}
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.confirmPassword && formik.errors.confirmPassword}
              helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
              rightSlot={
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                </button>
              }
            />

            {/* Checkbox Termos de Uso */}
            <div style={{ marginBottom: '16px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    sx={{
                      color: '#d1d5db',
                      '&.Mui-checked': { color: '#83d84d' },
                      padding: '2px 8px 2px 0',
                    }}
                  />
                }
                label={
                  <span style={{ fontSize: '13px', color: '#575757', lineHeight: '1.4' }}>
                    {t('auth.readAndAccept')}{' '}
                    <a
                      href="https://redutocapital-public-files.s3.us-east-1.amazonaws.com/Termos_e_Condicoes_de_Uso.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#83d84d', fontWeight: 600, textDecoration: 'underline' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t('auth.terms')}
                    </a>{' '}
                    {t('auth.privacy')}
                  </span>
                }
                sx={{ alignItems: 'flex-start' }}
              />
            </div>

            <Button type="submit" loading={isLoading} disabled={!acceptTerms}>
              {t('buttons.register')}
            </Button>

            <p className="text-center text-gray-500 text-sm mt-5">
              {t('auth.alreadyHaveAccount')}{' '}
              <Link
                to="/"
                className="text-[#83d84d] hover:text-[#72c73d] font-medium transition-colors"
              >
                {t('auth.logIn')}
              </Link>
            </p>
            <Backdrop open={isLoading} style={{ zIndex: 9999 }}>
              <CircularProgress color="inherit" sx={{ color: '#83d84d' }} />
            </Backdrop>
          </form>
        </FormCard>
      )}
    </>
  );
}
